'use client';

import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { http } from '../../../lib/api/http';
import { V2, V4 } from '../../../lib/api/urls';
import { activeStatus } from '../../../constants/events';
import { Container } from '../../layout/Container';
import {
  DEVICE_SCREENS_MAX_STRING,
  DEVICE_SCREENS_MIN_STRING,
} from '../../../lib/consts';
import { BreadCrumbs } from '../../blocks/BreadCrumbs';
import { ShareBlock } from '../../blocks/ShareBlock';
import { Description } from '../../blocks/Description';
import { CommentsBlock } from '../../comments/CommentsBlock';
import { Subscription } from '../../about/Subscription';
import { MobileApps } from '../../about/MobileApps';
import { PersonBanner } from '../../blocks/PersonBanner';
import { Posts } from '../../posts/posts';
import { ReadMore } from '../../blocks/ReadMore';
import { PictureModal } from '../../picture/PictureModal';
import { ImagesSlider } from '../../actions/images-slider';
import { Action, City, Person } from '../../../models';
import { getEvents } from '../../../lib/services/backend';

type IPersonPageProps = {
  currentCity: City;
  personData: Person;
};

export function PersonPage(props: IPersonPageProps) {
  const { currentCity, personData } = props;
  const theme = useTheme();
  const [events, setEvents] = useState<{ results: Action[] } | null>(null);
  const [imageModal, setImageModal] = useState<{
    slide: number | null;
    visible: boolean;
  }>({
    slide: null,
    visible: false,
  });

  useEffect(() => {
    http.post(V2.eventViews, {
      Person: personData.id,
    });

    getEvents(
      {
        pref_city: currentCity.id,
        status: ['active', 'archive'],
        persons: personData.id,
        limit: 8,
      },
      { arrayFormat: 'comma' }
    ).then((res) => setEvents(res));
  }, []);

  return (
    <Container
      className='container'
      css={{
        padding: '15px 8px',
        [DEVICE_SCREENS_MIN_STRING.xxl]: {
          maxWidth: '1280px !important',
        },
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '15px 16px',
        },
        [DEVICE_SCREENS_MAX_STRING.sm]: {
          padding: '0 !important',
        },
      }}
    >
      <div
        css={{
          marginBottom: 16,
          display: 'block',
          [DEVICE_SCREENS_MAX_STRING.lg]: { display: 'none' },
        }}
      >
        <BreadCrumbs
          currentCity={currentCity}
          items={[
            {
              title: 'Каталог персон',
              href: '/person-catalog/',
            },
            {
              title: `${personData.last_name} ${personData.first_name} ${personData.patronymic}`,
            },
          ]}
        />
      </div>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <PersonBanner item={personData} />
        <div
          itemScope
          itemType='https://schema.org/Person'
          css={{
            display: 'flex',
            marginTop: 30,
            marginBottom: 50,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              flexDirection: 'column',
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              marginTop: 8,
              padding: '0 8px',
            },
          }}
        >
          <span
            css={{ display: 'none' }}
            itemProp='name'
          >{`${personData.last_name} ${personData.first_name} ${personData.patronymic}`}</span>
          <div className='col-12 col-md-5 col-lg-4 col-xl-3'>
            <div css={{ marginBottom: 15 }}>
              <ShareBlock
                item={{
                  title: [personData.first_name, personData.last_name].join(
                    ' '
                  ),
                }}
              />
            </div>
            {personData.date_of_birth && (
              <div
                css={{
                  backgroundColor: theme.colors.white,
                  borderRadius: 4,
                  padding: 30,
                  marginBottom: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  fontFamily: 'Golos',
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '20px',
                  letterSpacing: '0em',
                  color: '#333',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    padding: 20,
                  },
                }}
              >
                <div
                  css={{
                    fontFamily: 'Montserrat',
                    color: theme.colors.red,
                    fontSize: 12,
                  }}
                >
                  Дата рождения
                </div>
                <span css={{ display: 'none' }} itemProp='birthDate'>
                  {personData.date_of_birth}
                </span>
                <div>
                  {DateTime.fromISO(personData.date_of_birth)
                    .setLocale('ru')
                    .toFormat('d MMMM yyyy')}{' '}
                  г.
                </div>
              </div>
            )}
          </div>
          <div
            className='col-12 col-md-6 push-md-1 col-lg-7 push-lg-1'
            css={{
              position: 'relative',
              left: '8.33333333%',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                left: 0,
              },
            }}
          >
            <div
              css={{
                marginBottom: 40,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  '.crop-text': { margin: '0 8px 40px 8px' },
                },
              }}
            >
              <Description
                title='Описание'
                description={
                  personData.description_html ?? personData.description
                }
              />
              <div
                css={{ display: 'none' }}
                itemProp='description'
                dangerouslySetInnerHTML={{
                  __html: `${
                    personData.description_html
                      ? `${personData.description.slice(0, 999)}...`
                      : personData.description.slice(0, 999)
                  } + '...'}`,
                }}
              />
            </div>
            {(personData.photos.length > 1 || personData.video_url) && (
              <ImagesSlider
                videoInfo={{
                  video: personData.video_url,
                  cover: personData.video_cover,
                }}
                images={personData.photos}
                onSlideClick={(index) =>
                  setImageModal({ slide: index, visible: true })
                }
              />
            )}
            <div>
              <CommentsBlock
                currentCity={currentCity}
                contentType='specializations.person'
                eventId={personData.id}
              />
            </div>
          </div>
        </div>
        <div
          css={{
            '.actions__item': {
              width: 320,
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              '.actions__item': {
                width: 270,
              },
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              padding: '0 8px',
              '.actions__item': {
                maxWidth: '77%',
                width: '100%',
              },
            },
          }}
        >
          {!!events?.results?.length && (
            <Posts
              title='Мероприятия'
              actions={events}
              href={['', currentCity.url, 'catalog', ''].join('/')}
              showOnlyCityInAddress
            />
          )}
        </div>
        {!!personData.materials.length && (
          <ReadMore
            place={null}
            materials={personData.materials}
            person={personData}
          />
        )}
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: '4rem',
            marginTop: '2rem',
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              gridTemplateColumns: '1fr',
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: { padding: '0 8px' },
          }}
        >
          <Subscription currentCity={currentCity} />
          <MobileApps />
        </div>
        {imageModal.visible &&
          (!!personData.photos?.length || personData.video_url) && (
            <PictureModal
              images={personData.photos.slice(1)}
              imageModal={imageModal}
              scrollY={window.scrollY}
              setVisible={setImageModal}
              videoInfo={{
                video: personData.video_url,
                cover: personData.video_cover,
              }}
            />
          )}
      </main>
    </Container>
  );
}
